import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Gift, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { LocalizedName } from '@/components/localized-name';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime, formatPhone, initials } from '@/lib/format';
import { toApiDate as apiDate } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import { localizedName } from '@/components/localized-name';
import { AssignGiftDialog } from './assign-gift-dialog';
import { useBookingLocations, useGiftSearch } from './gifts.api';
import { GIFT_STATUS, type SubscriptionGift } from './types';

/**
 * Membership gifts — plans bought for someone else, waiting to be attached to
 * a real account.
 *
 * Server-side search and paging: `subscriptions/gifts/search` takes the term,
 * status, location and a date range, and answers `{ total, gifts }`.
 */

const ANY = 'ANY';

/** Any / Pending / Assigned / Failed is a lifecycle; alphabetising would hide that. */
const STATUS_FILTERS = [
  { value: ANY, labelKey: 'gifts.any' },
  { value: String(GIFT_STATUS.pending), labelKey: 'gifts.pending' },
  { value: String(GIFT_STATUS.assigned), labelKey: 'gifts.assigned' },
  { value: String(GIFT_STATUS.failed), labelKey: 'gifts.failed' },
] as const;

function statusBadge(gift: SubscriptionGift, t: (k: string) => string) {
  if (gift.status === GIFT_STATUS.assigned) {
    return <Badge variant="success">{t('gifts.assigned')}</Badge>;
  }
  if (gift.status === GIFT_STATUS.failed) {
    return <Badge variant="destructive">{t('gifts.failed')}</Badge>;
  }
  return <Badge variant="warning">{t('gifts.pending')}</Badge>;
}

function firstOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
}

function lastOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);
}

export function MembershipGiftsPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  // Seeded once from the URL so the unredeemed-gifts figure on the
  // subscription sales report can hand over the set it counted.
  const [params] = useSearchParams();
  const [status, setStatus] = useState<string>(
    () => params.get('status') ?? ANY
  );
  const [locationId, setLocationId] = useState<string>(ANY);
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(lastOfMonth);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [assigning, setAssigning] = useState<SubscriptionGift | null>(null);

  const { data: locations, isPending: loadingLocations } = useBookingLocations();

  const input = useMemo(
    () => ({
      searchTerm: term,
      // "Any" is an omitted status, not -1 — the model types it as the enum.
      status: status === ANY ? null : Number(status),
      page,
      size,
      locationId: locationId === ANY ? null : locationId,
      startDate: apiDate(start, false),
      endDate: apiDate(end, true),
    }),
    [term, status, page, size, locationId, start, end]
  );

  const search = useGiftSearch(input, Boolean(start && end));

  const gifts = search.data?.gifts ?? [];
  const total = search.data?.total ?? 0;

  /** Any change to what is being asked for resets to the first page. */
  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const filterFields: FilterField[] = [
    {
      id: 'from',
      type: 'date',
      label: t('gifts.from'),
      value: start,
      onChange: reset(setStart),
      // A date has no "any"; it is never counted as narrowing.
      resetValue: start,
    },
    {
      id: 'to',
      type: 'date',
      label: t('gifts.to'),
      value: end,
      onChange: reset(setEnd),
      resetValue: end,
    },
    {
      id: 'location',
      label: t('gifts.location'),
      value: locationId,
      onChange: reset(setLocationId),
      disabled: loadingLocations,
      options: [
        { value: ANY, label: t('gifts.any') },
        ...(locations ?? []).map((location) => ({
          value: location.id,
          label: localizedName(location, language) || (location.name ?? ''),
        })),
      ],
    },
    {
      id: 'status',
      label: t('gifts.status'),
      value: status,
      onChange: reset(setStatus),
      // Any / Pending / Assigned / Failed is a lifecycle.
      sort: false,
      options: STATUS_FILTERS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('gifts.title')}
        description={t('gifts.subtitle')}
      />

      <FilterBar
        busy={search.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === term && page === 0) void search.refetch();
              setTerm(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('gifts.searchPlaceholder')}
                aria-label={t('gifts.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          search.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {t('gifts.showingOf', { shown: gifts.length, total })}
            </p>
          ) : null
        }
      />

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('gifts.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => void search.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {/* `isLoading`, not `isPending`: the search is off while a date is
          cleared, and a disabled query is pending for as long as it stays off. */}
      {search.isLoading && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-8 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-56" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && gifts.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-14 text-center">
            <span className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-full">
              <Gift className="size-7" />
            </span>
            <div className="space-y-1">
              <p className="font-medium">{t('gifts.noGiftsTitle')}</p>
              <p className="text-muted-foreground max-w-sm text-sm">
                {t('gifts.noGiftsBody')}
              </p>
            </div>
          </div>
        </div>
      )}

      {gifts.length > 0 && (
        <div className="space-y-3">
          <div className="hidden rounded-xl border lg:block">
            <Table busy={search.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('gifts.purchasedOn')}</TableHead>
                  <TableHead>{t('gifts.buyer')}</TableHead>
                  <TableHead>{t('gifts.recipient')}</TableHead>
                  <TableHead>{t('gifts.plan')}</TableHead>
                  <TableHead>{t('gifts.status')}</TableHead>
                  <TableHead className="text-right">
                    {t('gifts.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gifts.map((gift) => (
                  <TableRow key={gift.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTime(gift.createdAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar className="size-8 shrink-0">
                          {gift.buyer?.avatar && (
                            <AvatarImage src={gift.buyer.avatar} alt="" />
                          )}
                          <AvatarFallback>
                            {initials(
                              gift.buyer?.firstName,
                              gift.buyer?.lastName
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {gift.buyer?.completeName ?? '—'}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {gift.buyer?.email}
                          </p>
                          {gift.buyer?.phoneNumber && (
                            <p className="text-muted-foreground text-xs">
                              {formatPhone(gift.buyer.phoneNumber)}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">
                        {gift.recipientFullName ?? '—'}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {gift.recipientEmail}
                      </p>
                      {gift.recipientPhone && (
                        <p className="text-muted-foreground text-xs">
                          {formatPhone(gift.recipientPhone)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      {gift.subscription ? (
                        <LocalizedName
                          nameEn={gift.subscription.nameEn}
                          nameEs={gift.subscription.nameEs}
                          internalName={gift.subscription.internalName}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{statusBadge(gift, t)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        // Already attached to an account: nothing left to do.
                        disabled={Boolean(gift.assignedAt)}
                        onClick={() => setAssigning(gift)}
                      >
                        {t('gifts.assign')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={search.isFetching} />
            <div
              className={cn(
                'space-y-2',
                search.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {gifts.map((gift) => (
                <div
                  key={gift.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium">
                        {gift.recipientFullName ?? '—'}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {gift.recipientEmail}
                        {gift.recipientPhone && (
                          <span className="tabular-nums">
                            {' '}
                            · {formatPhone(gift.recipientPhone)}
                          </span>
                        )}
                      </p>
                    </div>
                    {statusBadge(gift, t)}
                  </div>

                  {gift.subscription && (
                    <LocalizedName
                      nameEn={gift.subscription.nameEn}
                      nameEs={gift.subscription.nameEs}
                      internalName={gift.subscription.internalName}
                    />
                  )}

                  {/* Who paid, kept distinct from who receives — on a gift those
                      are different people and the card has to say which is which. */}
                  <p className="text-muted-foreground text-xs">
                    {t('gifts.buyer')}: {gift.buyer?.completeName ?? '—'}
                    {gift.buyer?.email && ` · ${gift.buyer.email}`}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('gifts.purchasedOn')}: {formatDateTime(gift.createdAt)}
                  </p>

                  <Button
                    size="sm"
                    className="w-full"
                    disabled={Boolean(gift.assignedAt)}
                    onClick={() => setAssigning(gift)}
                  >
                    {t('gifts.assign')}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <AssignGiftDialog
        gift={assigning}
        open={assigning !== null}
        onOpenChange={(next) => {
          if (!next) setAssigning(null);
        }}
      />
    </div>
  );
}
